import { Component, createEffect, createMemo, createSignal, onMount, Show } from 'solid-js'
import { galleryStore } from '/web/store/gallery'
import { userStore } from '/web/store/user'
import { getAssetUrl } from '/web/shared/util'
import { NoTitleModal } from '/web/shared/Modal'
import { characterStore } from '/web/store'
import { AppSchema } from '/common/types'
import { GalleryImage } from '/common/types/aifans-schema'
import HeaderContainer from '/web/shared/aifans/HeaderContainer'
import ChatLeftNavContainer from './Chat/ChatLeftNavContainer'
import { Title, Meta, Link } from '@solidjs/meta'
import { useInfiniteScroll } from '/web/shared/hooks/useInfiniteScroll'
import LoadMoreButton from '/web/shared/aifans/model/buttons/LoadMoreButton'
import { getCanonicalUrl } from '/web/shared/aifans/lib/config-util'
import FlagIcon from '/web/shared/FlagIcon'
import TrashIcon from '/web/shared/TrashIcon'

const Gallery: Component = () => {
  const [selectedImage, setSelectedImage] = createSignal<GalleryImage | undefined>(undefined)
  const [charactersInFilter, setCharactersInFilter] = createSignal<AppSchema.Character[]>([])
  const [selectedCharacterFilter, setSelectedCharacterFilter] = createSignal<string | undefined>(
    undefined
  )

  const gallery = galleryStore((s) => ({
    images: s.images,
    loading: s.loading,
    showAllImages: s.showAllImages,
    showFlaggedImages: s.showFlaggedImages,
    hasMore: s.hasMore,
  }))

  const user = userStore((s) => ({
    user: s.user,
  }))

  // character store
  const chars = characterStore((s) => ({
    list: s.characters.list,
    map: s.characters.list.reduce<Record<string, AppSchema.Character>>(
      (prev, curr) => Object.assign(prev, { [curr._id]: curr }),
      {}
    ),
    loaded: s.characters.loaded,
  }))

  const handleToggleShowAll = async () => {
    galleryStore.toggleShowAllImages()
    await galleryStore.initPaged()
  }

  const handleToggleShowFlagged = async () => {
    galleryStore.toggleShowFlaggedImages()
    await galleryStore.initPaged()
  }

  const handleFlagImage = async (imageId: string, flagReason?: string) => {
    await galleryStore.flagImage(imageId, flagReason)
  }

  const handleUnflagImage = async (imageId: string) => {
    await galleryStore.unflagImage(imageId)
  }

  const handleDeleteImage = async (imageId: string) => {
    if (confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
      await galleryStore.deleteImage(imageId)
    }
  }

  onMount(async () => {
    if (!gallery.loading) {
      await galleryStore.initPaged()
    }

    if (!chars.loaded) {
      await characterStore.getCharacters()
    }
  })

  const loadMore = () => {
    if (!gallery.loading && gallery.hasMore) {
      galleryStore.loadMore()
    }
  }

  const { setTarget } = useInfiniteScroll(loadMore)

  const filteredImages = createMemo(
    () =>
      gallery?.images?.filter((image) => {
        // If showing flagged images, only show flagged ones
        if (gallery.showFlaggedImages) {
          if (!image.flagged) return false
        } else {
          // If not showing flagged images, hide flagged ones
          if (image.flagged) return false
        }

        // Character filter
        if (!selectedCharacterFilter()) {
          return true
        }

        if (image.characterId === selectedCharacterFilter()) {
          return true
        }

        return false
      }) || []
  )

  createEffect(() => {
    if (gallery.images?.length) {
      const characterIdCounts = gallery.images.reduce((acc: any, image) => {
        // If the characterId is already in the accumulator, increment its count
        if (acc[image.characterId]) {
          acc[image.characterId]++
        } else {
          // If the characterId is not in the accumulator, add it with a count of 1
          acc[image.characterId] = 1
        }
        return acc
      }, {})

      const characterData = chars.list
        .map((character) => {
          const count = characterIdCounts[character._id] || 0
          return {
            ...character,
            count,
          }
        })
        .filter((character) => character.count > 0)

      setCharactersInFilter(characterData)
    }
  })

  return (
    <>
      <Title>CosplayFans - Gallery | Explore Stunning Cosplay Creations</Title>
      <Meta name="og:title" content="CosplayFans - Gallery" />
      <Meta
        name="description"
        content="View your personal collection of AI-generated cosplay images. Access and manage all your custom character creations in one place."
      />
      <Meta
        name="og:description"
        content="View your personal collection of AI-generated cosplay images. Access and manage all your custom character creations in one place."
      />
      <Link rel="canonical" href={getCanonicalUrl('/gallery/')} />
      <div class="relative h-full w-full bg-black">
        <HeaderContainer />
        <ChatLeftNavContainer />
        <div
          id="main"
          class="flex flex-1 flex-col pt-14 md:min-h-[calc(100vh-82px)] md:pt-0 lg:ml-[222px]"
        >
          <div class="flex flex-col px-8 pt-4 md:mt-[82px] md:flex-row">
            <span class="flex flex-1 font-clash text-[28px] font-bold md:hidden md:text-[40px]">
              Gallery
            </span>
            <div class="flex flex-shrink-0 items-start justify-start md:mr-5">
              <GalleryCharacterFilter
                characters={charactersInFilter()}
                onSelect={(id) => setSelectedCharacterFilter(id)}
                selectedId={selectedCharacterFilter()}
                showAllImages={gallery.showAllImages}
                onToggleShowAll={handleToggleShowAll}
                showFlaggedImages={gallery.showFlaggedImages}
                onToggleShowFlagged={handleToggleShowFlagged}
                isAdmin={user.user?.admin}
              />
            </div>
            <div class="w-full">
              <span class="hidden font-clash text-[28px] font-bold md:flex md:flex-1 md:text-[40px]">
                Gallery
              </span>

              <div class="mx-auto mb-5 grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-4">
                {filteredImages().map((image) => (
                  <div class="relative">
                    <div
                      onClick={() => setSelectedImage(image)}
                      class="relative h-[250px] max-w-[400px] cursor-pointer overflow-hidden rounded-lg sm:h-[320px] md:h-[380px]"
                    >
                      <img
                        class="absolute h-full w-full object-cover"
                        src={getAssetUrl(image.url)}
                        alt="user"
                      />
                      {image.flagged && (
                        <div class="absolute right-2 top-2 rounded bg-red-500 px-2 py-1 text-xs font-bold text-white">
                          FLAGGED
                        </div>
                      )}
                      {user.user?.admin && (
                        <div class="absolute bottom-2 left-2 z-10 flex gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              if (image.flagged) {
                                handleUnflagImage(image._id)
                              } else {
                                const reason = prompt('Flag reason (optional):')
                                // Only flag if user didn't cancel (reason is not null)
                                if (reason !== null) {
                                  handleFlagImage(image._id, reason || undefined)
                                }
                              }
                            }}
                            class={`flex items-center justify-center rounded px-2 py-1 text-xs text-white hover:opacity-80 ${
                              image.flagged ? 'bg-green-500' : 'bg-red-500'
                            }`}
                            title={image.flagged ? 'Unflag image' : 'Flag image'}
                          >
                            <FlagIcon size={12} />
                          </button>
                          {image.flagged && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteImage(image._id)
                              }}
                              class="flex items-center justify-center rounded bg-gray-600 px-2 py-1 text-xs text-white hover:bg-red-600"
                              title="Delete image"
                            >
                              <TrashIcon size={12} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <Show when={gallery.hasMore && filteredImages().length > 0}>
                  <div class="col-span-full flex justify-center py-4">
                    <div ref={setTarget as any} />
                    <button onClick={loadMore}>
                      <LoadMoreButton />
                    </button>
                  </div>
                </Show>
                {filteredImages().length === 0 && (
                  <div class="col-span-full text-center text-white">
                    No images found.{' '}
                    {gallery.loading ? 'Loading...' : 'Try adjusting your filters.'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <NoTitleModal show={!!selectedImage()} close={() => setSelectedImage()} maxWidth="half">
        <Show when={selectedImage()}>
          {(imageAccessor) => {
            const image = imageAccessor()
            if (!image) return null

            const character = chars.map[image.characterId]
            const title = character?.name
            const createdAt = image.createdAt
              ? new Date(image.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })
              : 'Unknown date'

            return (
              <div class="flex flex-col p-4 pt-0">
                <div class="mb-4 flex flex-col gap-1">
                  <Show when={title}>
                    <h2 class="text-xl font-bold text-white">{title}</h2>
                  </Show>
                  <p class="text-sm text-gray-400">{createdAt}</p>
                  <Show when={image.prompt}>
                    <p class="text-xs text-gray-500">{image.prompt}</p>
                  </Show>
                </div>
                <div class="flex justify-center">
                  <img class="rounded-md" src={getAssetUrl(image.url)} alt={title} />
                </div>
              </div>
            )
          }}
        </Show>
      </NoTitleModal>
    </>
  )
}

const GalleryCharacterFilter = (props: {
  characters: AppSchema.Character[]
  selectedId?: string
  onSelect: (id?: string) => void
  showAllImages?: boolean
  onToggleShowAll?: () => void
  showFlaggedImages?: boolean
  onToggleShowFlagged?: () => void
  isAdmin?: boolean
}) => {
  const [showFilters, setShowFilters] = createSignal(false)

  const bgClass = 'bg-cosplay-gray-600'
  const bgClassSelected = 'bg-gradient-to-r from-cosplay-purple to-cosplay-blue-200'
  return (
    <div class="mb-5 flex flex-1 flex-col gap-2 rounded-md bg-cosplay-gray-700 px-8 py-3 text-[17px] md:mb-0 md:flex-none  md:py-6">
      {props.isAdmin && (
        <>
          <div class="mb-4">
            <div class="mb-2 font-bold">Admin Filter</div>
            <div class="flex flex-col gap-2">
              <button
                onClick={props.onToggleShowAll}
                class={`rounded-md px-4 py-2 ${!props.showAllImages ? bgClassSelected : bgClass}`}
              >
                My Images
              </button>
              <button
                onClick={props.onToggleShowAll}
                class={`rounded-md px-4 py-2 ${props.showAllImages ? bgClassSelected : bgClass}`}
              >
                All Images
              </button>
              <button
                onClick={props.onToggleShowFlagged}
                class={`rounded-md px-4 py-2 ${
                  props.showFlaggedImages ? bgClassSelected : bgClass
                }`}
              >
                Flagged Images
              </button>
            </div>
          </div>
          <div class="border-t border-cosplay-gray-600 pt-4"></div>
        </>
      )}

      <button onClick={() => setShowFilters(!showFilters())} class="font-bold md:cursor-default">
        Character Filter
      </button>
      <div class={`${showFilters() ? 'flex' : 'hidden'} flex-col gap-2 md:flex`}>
        <button
          onClick={() => props.onSelect()}
          class={`rounded-md px-4 py-2 ${!props.selectedId ? bgClassSelected : bgClass}`}
        >
          All Characters
        </button>
        {props.characters
          ?.sort((a, b) => {
            return a.name.localeCompare(b.name, 'en', { numeric: true })
          })
          .map((c: any) => (
            <button
              onClick={() => props.onSelect(c._id)}
              class={`rounded-md bg-cosplay-gray-600 px-4 py-2 ${
                props.selectedId === c._id ? bgClassSelected : bgClass
              }`}
            >{`${c.name} ( ${c.count} )`}</button>
          ))}
      </div>
    </div>
  )
}

export default Gallery
